/* eslint-disable ember/classic-decorator-no-classic-methods, ember/no-computed-properties-in-native-classes, ember/no-get, ember/require-computed-property-dependencies */
import Controller from '@ember/controller';
import { tracked } from '@glimmer/tracking';
import { isArray } from '@ember/array';
import { sort, alias } from '@ember/object/computed';
import { action, computed } from '@ember/object';
import { isEmpty } from '@ember/utils';
import {
  getGradeForDisplay,
  getPossibleGrades,
  gradesReducer,
} from 'linced-app/utils/grades';
import GradingMethod from 'linced-app/enums/grading-method';
import Jurisdiction from '@linc-technologies/linced-common/enums/jurisdiction';
import { service } from '@ember/service';
import {
  filterBy,
  mapBy,
  uniq,
} from '@linc-technologies/linced-common/utils/arrays';

export default class AssessmentController extends Controller {
  queryParams = ['moduleId'];
  Jurisdiction = Jurisdiction;

  @service
  session;

  @alias('session.school')
  school;

  @alias('school.jurisdiction')
  jurisdiction;

  // moduleId determines which module to show a summary for (overview shown if empty).
  @tracked
  moduleId = null;

  GradingMethod = GradingMethod;

  @computed('model.{group.name,assessment.label}')
  get exportTitle() {
    return `${this.model.group.name} - Assessment Snapshots - ${this.model.assessment.label}`;
  }

  @alias('model.learners.length')
  numLearners;

  @computed('model.modules.[]')
  get modules() {
    return this.model.modules
      .slice()
      .sort(
        (a, b) =>
          a.sequence - b.sequence ||
          (a.label || '').localeCompare(b.label || ''),
      );
  }

  @computed('modules.[]')
  get viewOptions() {
    let options = this.modules.map((module) => {
      return {
        value: module.id,
        label: module.label,
      };
    });
    options.unshift({ value: null, label: 'Overview' });
    return options;
  }

  @computed
  get summaryFields() {
    if (this.jurisdiction === Jurisdiction.AU_NSW) {
      return Object.freeze([
        { label: 'ATSI', field: 'atsi' },
        { label: 'Gender', field: 'gender' },
      ]);
    }
    return Object.freeze([
      { label: 'Ethnicity', field: 'displayEthnicity' },
      { label: 'Gender', field: 'gender' },
    ]);
  }

  @computed('model.{grades.[],learners.[]}')
  get learnerGrades() {
    let members = this.get('model.learners') ?? [];
    if (members.length === 0) {
      // No current learners in group - don't bother trying to map anything.
      return {};
    }

    // Build initial empty map of personId -> array of grades.
    let map = members.reduce((map, person) => {
      map[person.id] = [];
      return map;
    }, {});

    // Populate with records.
    let grades = this.get('model.grades') ?? [];
    if (grades.length === 0) {
      // No grades records - just return the empty map.
      return map;
    }

    // Add grade records into the prebuilt map.
    return grades.reduce((map, grade) => {
      let personId = grade.person_id;
      // Ignore people who aren't current group members.
      if (isArray(map[personId])) {
        map[personId].push(grade);
      }
      return map;
    }, map);
  }

  learnersSortOrder = Object.freeze(['firstName', 'lastName']);

  @sort('model.learners', 'learnersSortOrder')
  sortedLearners;

  @computed('sortedLearners.[]', 'learnerGrades', 'modules.[]')
  get exportData() {
    return (this.sortedLearners ?? []).map((learner) => {
      let person = learner.getProperties([
        'id',
        'jurisdictionalId',
        'firstName',
        'lastName',
        'currentYearLevel',
        'gender',
        'jurisdictionalEthnicity',
      ]);
      let row = [
        person.jurisdictionalId,
        person.firstName,
        person.lastName,
        person.currentYearLevel,
        person.gender,
        person.jurisdictionalEthnicity,
      ];

      let grades = this.get(`learnerGrades.${person.id}`) ?? [];
      this.modules.forEach((module) => {
        let grade = grades.find((item) => item.module_id === module.id);
        if (isEmpty(grade)) {
          row.push('');
        } else {
          row.push(getGradeForDisplay(module, grade, true));
        }
      });

      return row;
    });
  }

  @action
  setModuleId(option) {
    this.moduleId = option.value;
  }

  @action
  getPossibleGrades(module) {
    return getPossibleGrades(module);
  }

  @action
  getSummary(moduleId, possibleGrades) {
    let grades = this.model.grades.filter(
      (item) => item.module_id === moduleId,
    );
    return grades.reduce(gradesReducer(possibleGrades), []);
  }

  @action
  getSummaryByField(moduleId, possibleGrades, field) {
    let learners = this.model.learners;
    let grades = this.model.grades.filter(
      (item) => item.module_id === moduleId,
    );
    let values = uniq(mapBy(learners, field));
    let summary = [];

    // Build up array of grade counts, per unique field value.
    values.forEach((value) => {
      let learnerIds = mapBy(filterBy(learners, field, value), 'id');
      let valueGrades = grades.filter((grade) => {
        return learnerIds.includes(grade.get('person.id'));
      });

      summary.push({
        label: value,
        grades: valueGrades.reduce(gradesReducer(possibleGrades), []),
      });
    });

    return summary;
  }

  @action
  getPercentageOfLearners(num) {
    let result = 0;
    if (num <= 0 || this.numLearners <= 0) {
      return result;
    }

    result = (num / this.numLearners) * 100;
    return result.toFixed(2);
  }
}
